import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/ui-system";
import { apiErrorMessage } from "@/components/EmailTemplateRecipients";
import {
  PRESETS, formatWhen, historyRows, presetRange, rangeError, rangeLabel, rangeParams,
} from "@/lib/sendHistory";
import { BarChart3, CornerDownRight, RefreshCw, TriangleAlert } from "lucide-react";

/**
 * Send history — how many times each General Email Template, and each Common Client
 * announcement type, was sent in an IST date range
 * (GET …/admin/template-send-stats, read from the send logs). One Trigger Now = one
 * send. "View logs" opens the Logs tab filtered to that row.
 */
export default function TemplateSendHistory({ onViewLogs }) {
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState(() => presetRange("all"));
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const invalid = rangeError(range);

  useEffect(() => {
    if (invalid) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    axios.get(`${API}/notification-config/admin/template-send-stats`, { params: rangeParams(range) })
      .then(({ data }) => { if (live) setStats(data); })
      .catch((err) => { if (live) setError(apiErrorMessage(err, "Could not load the send history")); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [range, invalid, reloadKey]);

  const choosePreset = (value) => { setPreset(value); setRange(presetRange(value)); };
  const setDay = (end, value) => { setPreset("custom"); setRange((r) => ({ ...r, [end]: value || null })); };

  const rows = useMemo(() => historyRows(stats), [stats]);
  const totals = stats?.totals;

  const columns = [
    {
      key: "label", header: "Template / type", primary: true,
      cell: (r) => (r.level === 0 ? <span className="font-semibold text-gray-900">{r.label}</span> : (
        <span className="inline-flex items-start gap-1.5 text-gray-700 md:pl-4">
          <CornerDownRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />{r.label}
        </span>
      )),
    },
    { key: "sends", header: "Times sent", align: "right", cell: (r) => <span className="tabular-nums">{r.sends}</span> },
    {
      key: "failed", header: "Failed", align: "right",
      cell: (r) => (r.failed
        ? <span className="tabular-nums font-semibold text-red-600">{r.failed}</span>
        : <span className="tabular-nums text-gray-400">0</span>),
    },
    {
      key: "emails", header: "Emails delivered", align: "right",
      cell: (r) => <span className="tabular-nums">{r.emails_delivered}</span>,
    },
    { key: "last", header: "Last sent", cell: (r) => <span className="whitespace-nowrap">{formatWhen(r.last_sent_at)}</span> },
    {
      key: "actions", header: "", isActions: true,
      cell: (r) => (r.sends ? (
        <Button type="button" variant="link" size="sm" className="h-auto p-0 text-teal-700"
          onClick={() => onViewLogs({
            notification_type: r.notification_type, announcement_type: r.announcement_type,
            label: r.level ? `${r.parentLabel} · ${r.label}` : r.label, ...range,
          })}
          data-testid={`send-history-logs-${r.key}`}>
          View logs →
        </Button>
      ) : null),
    },
  ];

  return (
    <Card data-testid="send-history">
      <CardHeader className="pb-3 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-teal-600 shrink-0" />Send history
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              How many times each template was sent — one Trigger Now is one send. {rangeLabel(range)} (IST).
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading || Boolean(invalid)}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
          </Button>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Date range">
            {PRESETS.map((p) => (
              <Button key={p.value} type="button" size="sm" variant={preset === p.value ? "default" : "outline"}
                className={preset === p.value ? "bg-teal-600 hover:bg-teal-700" : ""}
                aria-pressed={preset === p.value} onClick={() => choosePreset(p.value)}
                data-testid={`send-history-preset-${p.value}`}>
                {p.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-500">
              From
              <Input type="date" value={range.from || ""} max={range.to || undefined}
                onChange={(e) => setDay("from", e.target.value)} className="h-9 w-40 mt-0.5"
                data-testid="send-history-from" />
            </label>
            <label className="text-xs text-gray-500">
              To
              <Input type="date" value={range.to || ""} min={range.from || undefined}
                onChange={(e) => setDay("to", e.target.value)} className="h-9 w-40 mt-0.5"
                data-testid="send-history-to" />
            </label>
          </div>
        </div>
        {invalid && <p className="text-xs text-red-600" role="alert">{invalid}</p>}
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <TriangleAlert className="w-5 h-5 text-red-500" /><span className="flex-1">{error}</span>
            <Button type="button" variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>Retry</Button>
          </div>
        ) : (
          <>
            <DataTable columns={columns} data={rows} rowKey={(r) => r.key} loading={loading && !stats}
              density="compact" />
            {totals && (
              <p className="text-sm text-gray-700" data-testid="send-history-totals">
                <span className="font-semibold">Total:</span> {totals.sends} send(s) · {" "}
                <span className={totals.failed ? "text-red-600 font-medium" : ""}>{totals.failed} failed</span>
                {" · "}{totals.emails_delivered} email(s) delivered
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
